import type { LeadStatus } from '@tool-factory/registry';
import { useParams } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import { useLead, useUpdateLeadStatus } from '../lib/queries';

const STATUSES: LeadStatus[] = [
  'captured',
  'qualified',
  'rejected',
  'listed_for_sale',
  'sold',
  'delivered',
  'paid',
  'converted',
];

function DataRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border/50 py-3 last:border-b-0">
      <dt className="text-[10px] font-semibold uppercase tracking-[0.16em] text-foreground/55">
        {label}
      </dt>
      <dd className="text-[13px] text-foreground text-right">{value}</dd>
    </div>
  );
}

export function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const lead = useLead(id);
  const updater = useUpdateLeadStatus(id ?? '');

  if (lead.isLoading) return <div className="text-[13px] text-foreground/55">Loading…</div>;
  if (!lead.data) return <div className="text-[13px] text-foreground/55">Not found.</div>;
  const l = lead.data;

  return (
    <div>
      <PageHeader
        breadcrumb={`Tool Factory · Leads · ${l.id.slice(0, 8)}`}
        title={`Lead ${l.id.slice(0, 8)}`}
        subtitle={
          <span className="inline-flex items-center gap-3">
            <span className="font-mono text-[12px] text-foreground/65">
              {new Date(l.created_at).toLocaleString()}
            </span>
            <span className="text-[12px] uppercase tracking-[0.12em] text-foreground/55">
              {l.status}
            </span>
          </span>
        }
      />

      <section className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-border bg-card px-5 py-2">
          <dl>
            <DataRow label="Status" value={l.status} />
            <DataRow label="Email" value={l.email ?? '—'} />
            <DataRow label="Phone" value={l.phone ?? '—'} />
            <DataRow label="Score" value={l.score ?? '—'} />
            <DataRow label="Timeline" value={l.timeline ?? '—'} />
          </dl>
        </div>
        <div className="rounded-lg border border-border bg-card px-5 py-2">
          <dl>
            <DataRow label="Property type" value={l.property_type ?? '—'} />
            <DataRow
              label="Estimated value"
              value={
                l.estimated_value_cents
                  ? `$${(l.estimated_value_cents / 100).toLocaleString()}`
                  : '—'
              }
            />
            <DataRow
              label="Geo"
              value={[l.geo_city, l.geo_region, l.geo_country].filter(Boolean).join(', ') || '—'}
            />
            <DataRow label="Sold to" value={l.sold_to ?? '—'} />
            <DataRow
              label="Sale price"
              value={l.sale_price_cents ? `$${(l.sale_price_cents / 100).toLocaleString()}` : '—'}
            />
          </dl>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-[15px] font-semibold mb-3 tracking-tight">Tool output</h2>
        <pre className="rounded-lg border border-border bg-card p-4 font-mono text-[12px] text-foreground/85 overflow-auto">
          {JSON.stringify(l.tool_output, null, 2)}
        </pre>
      </section>

      <section>
        <h2 className="font-heading text-[15px] font-semibold mb-3 tracking-tight">
          Update status
        </h2>
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => {
            const active = s === l.status;
            return (
              <button
                key={s}
                type="button"
                onClick={() => updater.mutate({ status: s })}
                className={[
                  'inline-flex items-center rounded-md border px-3 py-[7px] font-heading text-[12px] font-semibold tracking-[0.02em] transition-colors duration-150',
                  active
                    ? 'border-gold bg-gold text-background'
                    : 'border-border bg-card text-foreground hover:bg-secondary/60',
                ].join(' ')}
              >
                {s}
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
